import {
  createAppSubscriptionV2,
  deleteAppSubscriptionV2,
  getAppSubscriptionsV2,
} from "@/lib/data/requests/firm/firm-app-subscriptions";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  type APIError,
  type APIResponse,
  type AppSubscription,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { toast } from "react-hot-toast";

export const getAppSubscriptionsQueryOptions = (enabled = true) => ({
  queryKey: [queryKeys.firmAppSubscriptions],
  queryFn: () => getAppSubscriptionsV2(),
  enabled,
});

export const useAppSubscriptions = (enabled = true) => {
  return useQuery(getAppSubscriptionsQueryOptions(enabled));
};

export const useCreateAppSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAppSubscriptionV2,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error connecting app");
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        [queryKeys.firmAppSubscriptions],
        (oldData?: APIResponse<AppSubscription[]>) => {
          if (!oldData) return oldData;
          const next = oldData.data.filter(
            (subscription) => subscription.appId !== response.data.appId,
          );
          return {
            ...oldData,
            data: [...next, response.data],
          };
        },
      );
      toast.success("App connected successfully");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmAppSubscriptions],
      });
    },
  });
};

export const useDeleteAppSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAppSubscriptionV2,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error disconnecting app");
    },
    onSuccess: (_response, variables) => {
      queryClient.setQueryData(
        [queryKeys.firmAppSubscriptions],
        (oldData?: APIResponse<AppSubscription[]>) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.filter(
              (subscription) => subscription.id !== variables.subscriptionId,
            ),
          };
        },
      );
      toast.success("App disconnected successfully");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmAppSubscriptions],
      });
    },
  });
};
